import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Collapse,
  Table as MuiTable,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  RowData,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Fragment, useEffect, useState } from "react";
import { CreditCard, Send, Sliders, Tag, UserPlus } from "react-feather";
import { useDebounce } from "usehooks-ts";
import { Employee } from "../utils/useData";
import { AddUser } from "./employees/AddUser";
import { ApplyTags } from "./modals/ApplyTags";
import { AssignToPlan } from "./modals/AssignToPlan";
import { IssueCards } from "./modals/IssueCards";
import { SendMessage } from "./modals/SendMessage";
import { Filter } from "./table/Filter";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterType?: "number" | "select" | "string";
    filterSelectOptions?: {
      option: string;
      value: string;
      category?: string;
    }[];
    filterNumberRange?: { min: number; max: number };
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank
  });

  return itemRank.passed;
};

type TableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  defaultSorting?: SortingState;
};

export const Table = <T extends unknown>({
  data,
  columns,
  defaultSorting = []
}: TableProps<T>) => {
  const [searchValue, setSearchValue] = useState("");
  const globalFilter = useDebounce<string>(searchValue, 200);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [assignTagsModalOpen, setAssignTagsModalOpen] = useState(false);
  const [assignToPlanModalOpen, setAssignToPlanModalOpen] = useState(false);
  const [sendMessageModalOpen, setSendMessageModalOpen] = useState(false);
  const [issueCardModalOpen, setIssueCardModalOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { getBooleanFlag } = useKindeAuth();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter
    },
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination: {
        pageIndex: page,
        pageSize: pageSize
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setSearchValue,
    globalFilterFn: fuzzyFilter,
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      columnVisibility: {
        Name: false
      }
    }
  });

  useEffect(() => {
    table.setPageIndex(page);
    table.setPageSize(pageSize);
  }, [page, pageSize, table, data]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        border: "1px solid",
        borderRadius: 1,
        borderColor: "divider",
        boxShadow: "inherit"
      }}
    >
      {/* Table header */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{
          padding: 2,
          overflowX: "auto"
        }}
      >
        <TextField
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={`Search ${
            table.getFilteredRowModel().rows.length
          } users`}
          InputProps={{
            startAdornment: <Search />
          }}
          size="small"
          sx={{ minWidth: "200px" }}
        />
        <Button
          variant="contained"
          onClick={() => setAddUserModalOpen(true)}
          endIcon={<UserPlus size={16} />}
          size="small"
        >
          Add user
        </Button>
      </Stack>

      {/* User Actions */}
      <Collapse
        in={(table.getSelectedRowModel().rows as Row<Employee>[]).length > 0}
        style={{
          marginLeft: "auto",
          marginRight: "auto"
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            padding: 2,
            overflowX: "auto",
            height: 72
          }}
        >
          <Button
            variant="contained"
            onClick={() => setAssignTagsModalOpen(true)}
            endIcon={<Tag size={16} />}
            size="small"
          >
            Apply Tags
          </Button>
          <Button
            variant="contained"
            onClick={() => setAssignToPlanModalOpen(true)}
            endIcon={<Sliders size={16} />}
            size="small"
          >
            Assign to plan
          </Button>
          <Fragment>
            <Button
              variant="contained"
              onClick={() => setSendMessageModalOpen(true)}
              endIcon={<Send size={16} />}
              size="small"
            >
              Send message
            </Button>
            {getBooleanFlag("issue_card_button") && (
              <Button
                variant="contained"
                onClick={() => setIssueCardModalOpen(true)}
                endIcon={<CreditCard size={16} />}
                size="small"
              >
                Issue cards
              </Button>
            )}
          </Fragment>
        </Stack>
      </Collapse>

      {/* Main table */}
      <TableContainer>
        <MuiTable>
          <TableHead
            sx={{
              backgroundColor: "grey.50",
              borderTop: 1,
              borderBottom: 1,
              borderColor: "grey.200"
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{ borderBottom: 1, borderColor: "grey.200" }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        {header.column.getCanSort() ? (
                          <TableSortLabel
                            active={header.column.getIsSorted() !== false}
                            direction={
                              header.column.getIsSorted() === "asc"
                                ? "asc"
                                : "desc"
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getIsSorted() !== false ? (
                              <Box component="span" sx={visuallyHidden}>
                                {header.column.getIsSorted() === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        ) : (
                          <>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </>
                        )}
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody
            sx={{
              "> tr, th": {
                borderColor: "grey.200"
              }
            }}
          >
            {/* filters */}
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{ borderBottom: 1, borderColor: "grey.200" }}
                  >
                    <Filter header={header} column={header.column} />
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* rows */}
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    sx={{ borderBottom: 1, borderColor: "grey.200" }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        component="div"
        count={table.getFilteredRowModel().rows.length}
        page={table.getState().pagination.pageIndex}
        rowsPerPage={table.getState().pagination.pageSize}
        onPageChange={(_, page) => setPage(page)}
        onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value))}
      />
      <AddUser
        open={addUserModalOpen}
        handleClose={() => setAddUserModalOpen(false)}
      />
      {assignTagsModalOpen && (
        <ApplyTags
          open={true}
          handleClose={() => setAssignTagsModalOpen(false)}
          selectedEmployees={table
            .getSelectedRowModel()
            .rows.map((row) => row.original)}
        />
      )}
      {assignToPlanModalOpen && (
        <AssignToPlan
          open={true}
          handleClose={() => setAssignToPlanModalOpen(false)}
          selectedEmployees={table
            .getSelectedRowModel()
            .rows.map((row) => row.original)}
        />
      )}
      {sendMessageModalOpen && (
        <SendMessage
          open={true}
          handleClose={() => setSendMessageModalOpen(false)}
          selectedEmployees={table
            .getSelectedRowModel()
            .rows.map((row) => row.original)}
        />
      )}
      {issueCardModalOpen && (
        <IssueCards
          open={true}
          handleClose={() => setIssueCardModalOpen(false)}
          selectedEmployees={table
            .getSelectedRowModel()
            .rows.map((row) => row.original)}
        />
      )}
    </Card>
  );
};
